import { useEffect, useState } from "react";
import { api, errText, photoUrl } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { toast } from "sonner";
import { Loader2, Megaphone, ImagePlus, X, Plus, MousePointerClick, Eye, GripVertical, TrendingUp, Smartphone, Copy, LayoutTemplate } from "lucide-react";
import { CATEGORIES } from "../../lib/constants";
import { TemplatesGallery } from "./HeroTemplates";

// "YYYY-MM-DDTHH:MM" in Toronto time -> UTC ISO string.
const torontoToISO = (v) => {
  if (!v) return "";
  const guess = new Date(`${v}:00Z`);
  const tz = new Date(guess.toLocaleString("en-US", { timeZone: "America/Toronto" }));
  const utc = new Date(guess.toLocaleString("en-US", { timeZone: "UTC" }));
  return new Date(guess.getTime() + (utc - tz)).toISOString();
};

const SCOPES = [{ id: "home", label: "Homepage" }, ...CATEGORIES.filter((c) => c.id !== "all").map((c) => ({ id: c.id, label: c.label }))];
const scopeLabel = (id) => (SCOPES.find((s) => s.id === id) || SCOPES[0]).label;

const blank = () => ({ id: "", enabled: true, image: "", mobile_image: "", link: "", alt: "", start_at: "", end_at: "", weight: 1, clicks: 0, impressions: 0, ctr: 0 });

export function MarketingBanner() {
  const { user } = useAuth();
  const [banners, setBanners] = useState(null);
  const [abOptimize, setAbOptimize] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingKey, setUploadingKey] = useState("");
  const [dragIdx, setDragIdx] = useState(null);
  const [promo, setPromo] = useState(null);
  const [scope, setScope] = useState("home");
  const [tplOpen, setTplOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [copyOpen, setCopyOpen] = useState(false);
  const [copying, setCopying] = useState("");

  const copyTo = async (target) => {
    const copies = banners.filter((b) => b.image).map((b) => ({
      enabled: b.enabled, image: b.image, mobile_image: b.mobile_image, link: b.link,
      alt: b.alt, start_at: b.start_at, end_at: b.end_at, weight: b.weight,
    }));
    if (!copies.length) { toast.error("Add a banner first"); return; }
    setCopying(target);
    try {
      const qs = target !== "home" ? `?category=${target}` : "";
      const { data: cur } = await api.get(`/admin/site/marketing-banner${qs}`);
      const merged = [...(cur.banners || []), ...copies].slice(0, 10);
      await api.put(`/admin/site/marketing-banner${qs}`, { banners: merged, ab_optimize: !!cur.ab_optimize });
      toast.success(`Copied ${copies.length} banner${copies.length === 1 ? "" : "s"} to ${scopeLabel(target)}`);
      setCopyOpen(false);
    } catch (e) { toast.error(errText(e)); }
    finally { setCopying(""); }
  };

  useEffect(() => {
    if (user?.role !== "admin") return;
    setBanners(null);
    const qs = scope !== "home" ? `?category=${scope}` : "";
    api.get(`/admin/site/marketing-banner${qs}`).then((r) => { setBanners(r.data.banners || []); setAbOptimize(!!r.data.ab_optimize); }).catch(() => setBanners([]));
  }, [user, scope]);

  useEffect(() => {
    if (user?.role !== "admin") return;
    api.get("/admin/site/verify-promo").then((r) => setPromo(r.data.variants || [])).catch(() => {});
  }, [user]);

  const setField = (i, k, v) => setBanners((bs) => bs.map((b, idx) => idx === i ? { ...b, [k]: v } : b));
  const addBanner = () => setBanners((bs) => [...bs, blank()]);
  const addFromTemplate = async (s, name) => {
    setGenerating(true);
    try {
      const btn = (s.buttons || [])[0];
      const { data } = await api.post("/admin/seasonal-banner", { theme: s.template_key || "default", badge: s.badge, title_line1: s.title_line1, title_line2: s.title_line2, button_label: btn?.label || "", tall: scope === "lost-found" });
      setBanners((bs) => [...bs, { ...blank(), image: data.image, mobile_image: data.mobile_image, link: btn?.link || "", alt: `${s.title_line1} ${s.title_line2}`.trim(), start_at: torontoToISO(s.start_at), end_at: torontoToISO(s.end_at) }]);
      toast.success(`"${name}" banner generated — review it, then click Save banners`);
    } catch (e) { toast.error(errText(e)); }
    finally { setGenerating(false); }
  };
  const removeBanner = (i) => setBanners((bs) => bs.filter((_, idx) => idx !== i));
  const moveBanner = (from, to) => setBanners((bs) => {
    if (from === to || from == null || to == null) return bs;
    const next = [...bs];
    const [m] = next.splice(from, 1);
    next.splice(to, 0, m);
    return next;
  });

  const onImage = async (e, i, field = "image") => {
    const file = (e.target.files || [])[0];
    e.target.value = "";
    if (!file) return;
    setUploadingKey(`${i}:${field}`);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setField(i, field, data.path);
      toast.success(field === "mobile_image" ? "Mobile image uploaded" : "Banner image uploaded");
    } catch (err) { toast.error(errText(err)); }
    finally { setUploadingKey(""); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { banners: banners.filter((b) => b.image), ab_optimize: abOptimize };
      const qs = scope !== "home" ? `?category=${scope}` : "";
      const { data } = await api.put(`/admin/site/marketing-banner${qs}`, payload);
      setBanners(data.banners || []);
      setAbOptimize(!!data.ab_optimize);
      toast.success(`${scopeLabel(scope)} banners saved`);
    } catch (e) { toast.error(errText(e)); }
    finally { setSaving(false); }
  };

  if (user?.role !== "admin") return null;

  return (
    <div className="max-w-5xl mt-6" data-testid="marketing-banner-settings">
      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Megaphone size={18} className="text-[#F5551E]" />
            <h3 className="font-display font-bold text-[#12234A]">{scopeLabel(scope)} featured banners</h3>
          </div>
          <label className="flex items-center gap-2 text-xs font-semibold text-slate-500">
            Showcase on:
            <select value={scope} onChange={(e) => setScope(e.target.value)} data-testid="marketing-scope-select"
              className="rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-sm font-semibold text-[#12234A] focus:outline-none focus:ring-2 focus:ring-[#F5551E]/30">
              {SCOPES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          </label>
        </div>
        <p className="text-xs text-slate-500 mt-1">{scope === "home"
          ? "An admin-only space on the homepage for your own promos — separate from the paid ads advertisers book. Add several and they rotate in the same spot. Drag to set the order, and raise a banner's weight to show it more often. Recommended size: 1200×200px."
          : scope === "lost-found"
          ? "The full-width alert header at the top of the Lost & Found page. Add one or more banners to replace the built-in red alert header; remove them all to bring it back. Recommended size: 1200×200px (mobile 800×500px)."
          : `A rotating "featured" banner shown at the bottom of the ${scopeLabel(scope)} category page, at half the homepage height. Add several and they rotate; drag to order and raise weight to show more often. Recommended size: 1200×100px.`}</p>

        {!banners ? (
          <div className="h-40 flex items-center justify-center"><Loader2 className="animate-spin text-[#F5551E]" /></div>
        ) : (<>
        <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50/70 p-3 flex items-start gap-3" data-testid="marketing-ab-optimize-box">
          <TrendingUp size={18} className="text-[#F5551E] mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-3">
              <span className="text-sm font-bold text-[#12234A]">Auto-optimize by clicks (A/B)</span>
              <Switch checked={abOptimize} onCheckedChange={setAbOptimize} data-testid="marketing-ab-optimize-toggle" />
            </div>
            <p className="text-xs text-slate-500 mt-1">When on, your enabled banners still rotate, but the ones earning more clicks are shown more often automatically. New banners keep a fair share until they gather at least 20 views. Manual weights below are ignored while this is on.</p>
          </div>
        </div>

        <div className="mt-4 space-y-4">
          {banners.length === 0 && (
            <div className="rounded-xl border-2 border-dashed border-slate-200 p-8 text-center text-sm text-slate-400" data-testid="marketing-empty">No banners yet. Add one to get started.</div>
          )}
          {banners.map((b, i) => {
            const img = b.image ? photoUrl(b.image) : "";
            const mImg = b.mobile_image ? photoUrl(b.mobile_image) : "";
            return (
              <div key={b.id || i} data-testid={`marketing-banner-row-${i}`}
                draggable
                onDragStart={() => setDragIdx(i)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); moveBanner(dragIdx, i); setDragIdx(null); }}
                onDragEnd={() => setDragIdx(null)}
                className={`rounded-xl border p-4 transition-colors ${dragIdx === i ? "border-[#F5551E] bg-[#FFF7F4] opacity-70" : "border-slate-200"}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className="cursor-grab active:cursor-grabbing text-slate-300 hover:text-slate-500" data-testid={`marketing-drag-${i}`} title="Drag to reorder"><GripVertical size={16} /></span>
                    <span className="text-sm font-bold text-[#12234A]">Banner {i + 1}</span>
                    <Switch checked={b.enabled} onCheckedChange={(v) => setField(i, "enabled", v)} data-testid={`marketing-enabled-${i}`} />
                    <span className={`text-[11px] font-semibold ${b.enabled ? "text-emerald-600" : "text-slate-400"}`}>{b.enabled ? "On" : "Off"}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-[11px] text-slate-500 flex items-center gap-1" title="Impressions"><Eye size={13} /> {b.impressions ?? 0}</span>
                    <span className="text-[11px] text-slate-500 flex items-center gap-1" title="Clicks"><MousePointerClick size={13} /> {b.clicks ?? 0}</span>
                    <span className="text-[11px] font-semibold text-[#F5551E]" title="Click-through rate">{b.ctr ?? 0}% CTR</span>
                    <button onClick={() => removeBanner(i)} data-testid={`marketing-remove-${i}`} className="text-slate-300 hover:text-red-500 transition-colors p-1 rounded-md hover:bg-red-50" title="Remove banner"><X size={16} /></button>
                  </div>
                </div>

                <div className="grid lg:grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="relative h-14 w-28 rounded-lg overflow-hidden border border-slate-200 bg-slate-100 shrink-0 flex items-center justify-center">
                        {img ? <img src={img} alt="" className="h-full w-full object-contain" /> : <span className="text-[10px] text-slate-400">No image</span>}
                      </div>
                      <div className="min-w-0">
                        <p className="text-[11px] font-semibold text-slate-500">Desktop <span className="font-normal text-slate-400">({scope === "lost-found" || scope === "home" ? "1200×200" : "1200×100"})</span></p>
                        <label data-testid={`marketing-image-upload-${i}`} className="flex items-center gap-1.5 text-sm font-semibold text-[#F5551E] cursor-pointer hover:underline">
                          {uploadingKey === `${i}:image` ? <Loader2 size={15} className="animate-spin" /> : <ImagePlus size={15} />} {b.image ? "Replace" : "Upload"}
                          <input type="file" accept="image/*" onChange={(e) => onImage(e, i, "image")} className="hidden" />
                        </label>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="relative h-14 w-14 rounded-lg overflow-hidden border border-slate-200 bg-slate-100 shrink-0 flex items-center justify-center">
                        {mImg ? <img src={mImg} alt="" className="h-full w-full object-cover" /> : <Smartphone size={16} className="text-slate-300" />}
                      </div>
                      <div className="min-w-0">
                        <p className="text-[11px] font-semibold text-slate-500">Mobile <span className="font-normal text-slate-400">(optional · ~{scope === "lost-found" || scope === "home" ? "800×500" : "800×250"})</span></p>
                        <div className="flex items-center gap-2">
                          <label data-testid={`marketing-mobile-upload-${i}`} className="flex items-center gap-1.5 text-sm font-semibold text-[#F5551E] cursor-pointer hover:underline">
                            {uploadingKey === `${i}:mobile_image` ? <Loader2 size={15} className="animate-spin" /> : <Smartphone size={15} />} {b.mobile_image ? "Replace" : "Upload"}
                            <input type="file" accept="image/*" onChange={(e) => onImage(e, i, "mobile_image")} className="hidden" />
                          </label>
                          {b.mobile_image && <button onClick={() => setField(i, "mobile_image", "")} data-testid={`marketing-mobile-clear-${i}`} className="text-[11px] text-slate-400 hover:text-red-500">Remove</button>}
                        </div>
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs">Link when clicked (optional)</Label>
                      <Input value={b.link} onChange={(e) => setField(i, "link", e.target.value)} data-testid={`marketing-link-${i}`} placeholder="https://example.com/promo" className="mt-1 h-9 rounded-lg text-sm" />
                    </div>
                    <div>
                      <Label className="text-xs">Alt text</Label>
                      <Input value={b.alt} onChange={(e) => setField(i, "alt", e.target.value)} data-testid={`marketing-alt-${i}`} placeholder="Describe the banner" className="mt-1 h-9 rounded-lg text-sm" />
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs">Show from (optional)</Label>
                        <Input type="datetime-local" value={toLocal(b.start_at)} onChange={(e) => setField(i, "start_at", e.target.value)} data-testid={`marketing-start-${i}`} className="mt-1 h-9 rounded-lg text-sm" />
                      </div>
                      <div>
                        <Label className="text-xs">Hide after (optional)</Label>
                        <Input type="datetime-local" value={toLocal(b.end_at)} onChange={(e) => setField(i, "end_at", e.target.value)} data-testid={`marketing-end-${i}`} className="mt-1 h-9 rounded-lg text-sm" />
                      </div>
                    </div>
                    <div className={abOptimize ? "opacity-50 pointer-events-none" : ""}>
                      <Label className="text-xs">Rotation weight <span className="text-slate-400 font-normal">(1–10 · higher shows more often)</span></Label>
                      <div className="mt-1 flex items-center gap-2">
                        <input type="range" min="1" max="10" value={b.weight || 1} onChange={(e) => setField(i, "weight", parseInt(e.target.value, 10))} data-testid={`marketing-weight-${i}`} className="flex-1 accent-[#F5551E]" />
                        <span className="text-sm font-bold text-[#12234A] w-6 text-center">{b.weight || 1}</span>
                      </div>
                    </div>
                    <div className="rounded-lg overflow-hidden border border-slate-200">
                      {img ? <img src={img} alt={b.alt} className={`w-full ${scope === "lost-found" || scope === "home" ? "aspect-[6/1]" : "aspect-[12/1]"} object-cover`} />
                        : <div className={`${scope === "lost-found" || scope === "home" ? "aspect-[6/1]" : "aspect-[12/1]"} bg-slate-50 flex items-center justify-center text-xs text-slate-400`}>Preview</div>}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex items-center gap-2 mt-4 flex-wrap">
          <Button variant="outline" onClick={addBanner} data-testid="marketing-add-btn" className="rounded-xl gap-1.5"><Plus size={15} /> Add banner</Button>
          {scope !== "home" && (
            <Button variant="outline" onClick={() => setTplOpen(true)} disabled={generating} data-testid="marketing-templates-btn" className="rounded-xl gap-1.5">
              {generating ? <Loader2 size={15} className="animate-spin" /> : <LayoutTemplate size={15} />} Templates
            </Button>
          )}
          <TemplatesGallery open={tplOpen} onOpenChange={setTplOpen} onPick={addFromTemplate} noToast title="Seasonal banner templates" addLabel="Add banner"
            description="Picking a template generates matching desktop and mobile banner images, pre-scheduled for its next upcoming date (Toronto time). You can replace the images before saving."
            disabled={(banners || []).length >= 10} maxNote="This category already has the maximum of 10 banners. Delete one to add a template." />
          <Button onClick={save} disabled={saving} data-testid="marketing-save-btn" className="rounded-xl bg-[#F5551E] hover:bg-[#E04812] text-white font-semibold gap-2">
            {saving ? <Loader2 size={16} className="animate-spin" /> : "Save banners"}
          </Button>
          <Button variant="outline" onClick={() => setCopyOpen((v) => !v)} data-testid="marketing-copy-btn" disabled={!banners.some((b) => b.image)} className="rounded-xl gap-1.5 ml-auto"><Copy size={15} /> Copy to…</Button>
        </div>
        {copyOpen && (
          <div className="mt-2 rounded-xl border border-slate-200 bg-slate-50/70 p-3" data-testid="marketing-copy-targets">
            <p className="text-xs text-slate-500 mb-2">Copy this scope's banners (as fresh copies) into another scope — they're added to whatever's already there.</p>
            <div className="flex flex-wrap gap-2">
              {SCOPES.filter((s) => s.id !== scope).map((s) => (
                <button key={s.id} onClick={() => copyTo(s.id)} disabled={!!copying} data-testid={`marketing-copy-to-${s.id}`}
                  className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-[#12234A] hover:border-[#F5551E] hover:text-[#F5551E] transition-colors disabled:opacity-50 inline-flex items-center gap-1.5">
                  {copying === s.id ? <Loader2 size={13} className="animate-spin" /> : null} {s.label}
                </button>
              ))}
            </div>
          </div>
        )}
        </>)}
      </div>

      {/* Home "Get Verified" promo headline A/B results */}
      {scope === "home" && (
      <div className="bg-white rounded-2xl border border-slate-200 p-5 mt-6" data-testid="verify-promo-ab">
        <div className="flex items-center gap-2">
          <TrendingUp size={18} className="text-[#F5551E]" />
          <h3 className="font-display font-bold text-[#12234A]">"Get Verified" home banner — headline A/B</h3>
        </div>
        <p className="text-xs text-slate-500 mt-1">Each visitor randomly sees one of two headlines on the homepage "Get verified" banner. Here's how each is performing — the higher click-through rate wins.</p>
        {!promo ? (
          <div className="h-16 flex items-center text-sm text-slate-400">Loading…</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
            {promo.map((v) => {
              const headline = PROMO_HEADLINES[v.variant] || v.variant;
              const best = Math.max(...promo.map((x) => x.ctr), 0);
              const leading = v.ctr > 0 && v.ctr === best && promo.some((x) => x.variant !== v.variant && x.ctr !== v.ctr);
              return (
                <div key={v.variant} data-testid={`promo-variant-${v.variant}`} className={`rounded-xl border p-4 ${leading ? "border-[#F5551E] ring-1 ring-[#F5551E]/30 bg-[#FFF9F6]" : "border-slate-200"}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold uppercase tracking-wide text-slate-400">Variant {v.variant.toUpperCase()}</span>
                    {leading && <span className="text-[10px] font-bold bg-[#F5551E] text-white px-2 py-0.5 rounded-full">Leading</span>}
                  </div>
                  <p className="font-display font-bold text-[#12234A] mt-1.5 leading-snug">"{headline}"</p>
                  <div className="flex items-center gap-4 mt-3 text-sm">
                    <span className="inline-flex items-center gap-1.5 text-slate-600"><Eye size={15} className="text-slate-400" /> {v.impressions.toLocaleString()}</span>
                    <span className="inline-flex items-center gap-1.5 text-slate-600"><MousePointerClick size={15} className="text-slate-400" /> {v.clicks.toLocaleString()}</span>
                    <span className="ml-auto font-bold text-[#12234A]">{v.ctr}% CTR</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      )}
    </div>
  );
}

const PROMO_HEADLINES = {
  a: "Get verified and stand out",
  b: "Turn browsers into buyers — get verified",
};

// ISO (or "") <-> value for <input type=datetime-local> (local time, no seconds)
function toLocal(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
